import React, { useState } from "react";
import { useFetch } from "@/hooks/useApi";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Loader, StatusPill, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { num, ukDate } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function Stops() {
  const [tab, setTab] = useState("pending");
  const { data: stops, loading, refetch } = useFetch("/stops");
  const [approve, setApprove] = useState(null);
  const [val, setVal] = useState("");

  const openApprove = (s) => { setApprove(s); setVal(String(s.submitted_stops)); };
  const doApprove = async () => {
    try {
      await api.put(`/stops/${approve.id}/approve`, { approved_stops: parseInt(val, 10) });
      toast.success("Stops approved — earnings updated");
      setApprove(null); refetch();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <Loader />;
  const filtered = (stops || []).filter((s) => (tab === "all" ? true : s.status === tab));
  const pendingCount = (stops || []).filter((s) => s.status === "pending").length;

  return (
    <div className="fp-fade-in">
      <PageHeader title="Daily Stops" subtitle="Review and approve driver stop submissions" testId="stops-page" />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList data-testid="stops-tabs">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending {pendingCount > 0 && `(${pendingCount})`}</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Van</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Approved</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => (
                <TableRow key={s.id} data-testid={`stop-row-${s.id}`}>
                  <TableCell className="font-mono text-sm">{ukDate(s.date)}</TableCell>
                  <TableCell className="font-medium">{s.driver_name}</TableCell>
                  <TableCell>{s.van_reg ? <Plate reg={s.van_reg} /> : "—"}</TableCell>
                  <TableCell className="font-mono">{num(s.submitted_stops)}</TableCell>
                  <TableCell className="font-mono font-semibold text-emerald-400">{s.approved_stops != null ? num(s.approved_stops) : "—"}</TableCell>
                  <TableCell><StatusPill status={s.status} /></TableCell>
                  <TableCell className="text-right">
                    {s.status === "pending" ? (
                      <Button size="sm" onClick={() => openApprove(s)} data-testid={`approve-stop-${s.id}`}>
                        <Icons.Check className="h-4 w-4 mr-1" /> Confirm
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => openApprove(s)}>Adjust</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {filtered.length === 0 && <EmptyState icon="MapPin" title="No stops here" subtitle="Nothing to show for this filter." />}
      </Card>

      <Dialog open={!!approve} onOpenChange={() => setApprove(null)}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader><DialogTitle>Confirm Stops</DialogTitle></DialogHeader>
          {approve && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{approve.driver_name} · {ukDate(approve.date)}</span>
                {approve.van_reg && <Plate reg={approve.van_reg} />}
              </div>
              <div className="rounded-lg bg-muted p-4 flex items-center justify-between">
                <span className="text-sm">Driver submitted</span>
                <span className="font-mono text-2xl font-bold">{num(approve.submitted_stops)}</span>
              </div>
              <div>
                <Label>Approved number of stops</Label>
                <Input type="number" value={val} onChange={(e) => setVal(e.target.value)} className="mt-1.5 font-mono text-lg" data-testid="approved-stops-input" />
                <p className="text-xs text-muted-foreground mt-1.5">Only the approved number is used to calculate earnings.</p>
              </div>
              {approve.notes && <p className="text-sm text-muted-foreground">Notes: {approve.notes}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprove(null)}>Cancel</Button>
            <Button onClick={doApprove} data-testid="confirm-approve-button">Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
